import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { CardControl } from 'projects/money-movement-ui/src/app/shared/models/CardControl';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Classifications } from '../../../account-info-header/models';
import { AccountInfoState } from '../../../account-info-header/models/AccountInfoState';
import { selectClassifications, selectFilteredItStandingAuth, selectHasBundled, selectInfo, selectLoadPopupCallFinished } from '../../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement, selectHasPositionJournalLotSelection, selectHasPositionLotsPhase2 } from '../../../authorize/reducers';
import * as radioModels from '../../../shared/controlModels/radioModels';
import { selectStandingAuthTableStartTransactionClickedInternalTransfer } from '../../../shared/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { StandingAuthorizationCard } from '../../../shared/components/standing-authorization-card-container/standing-authorization-card-container.component';
import { InternalTransferActions, InternalTransfersActions, PtrsActions, StandingAuthorizationsActions, StandingAuthorizationsTableActions } from '../../actions';
import { StandingAuthorizationsRequest } from '../../models/Standing-Authorizations';
import { RadioControl } from '../../models/form/FormControlRadio';
import { ItCloseAccount, ItTransferType } from '../../models/form/FormControlTypes';
import { ItExtraPrompts, TransactionForm, TransactionFormServiceError } from '../../models/form/TransactionForm';
import { StandingAuthInputRequest } from '../../models/internal-transfer/InternalTransferInputRequest';
import { selectFrequency, selectInternalTransferInfo, selectIsFromEdit, selectIsOneTimeTotalDist, selectItStandingAuthCallSucceeded, selectJournalsUseStandingAuth, selectStandingAuthId, selectTransferType, selectUseStandingAuth } from '../../reducers';
import { buildFromPrefilledForm, createControl } from '../../utilities/formUtilities';
import { selectEnableUseSloaContainer, selectIsEnableTotalDistSloaBsloaIt } from '../../../feature-flags/reducers';
import { Constants, Frequencies, totalDistributionConstants } from '../../../shared/utilities/Constants';

@Component({
  standalone: false,
  selector: 'mm-it-extra-prompts',
  templateUrl: './it-extra-prompts.component.html',
  styleUrls: ['./it-extra-prompts.component.scss'],
})
export class ItExtraPromptsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Output() extraPromptsComplete = new EventEmitter<boolean>();
  @Input() prefilledPromptsObs: Observable<ItExtraPrompts>;
  @Input() editFormDetails: TransactionForm | undefined;
  @Output() extraPromptsUpdatedInEdit = new EventEmitter();

  showFrequencyControl: boolean;
  totalDistributionConstants = totalDistributionConstants;
  isFromEdit: boolean;
  accountInfo: AccountInfoState
  destroy$: Subject<boolean> = new Subject<boolean>();
  standingInstructions: StandingInstruction[];
  standingAuthCards: StandingAuthorizationCard[] = [];
  bundledStandingAuthCards: StandingAuthorizationCard[] = [];
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  showStandingAuthControl: boolean;
  loadPopupCallFinished$: Observable<boolean>;
  standingAuth: boolean;
  showTransferType: boolean;
  isOneTime: boolean = false;
  showPositionLotsFeature: boolean = false;
  classifications: Classifications;
  hasBundledOffer: boolean = false;
  showBundledStandingAuthSelection: boolean;
  useBundledPromptsSelected: boolean;
  bsloaModel = radioModels.bsloaModel;
  useBundledModel = radioModels.useBundledModel;
  hasMmInitiateAccess: boolean;
  totalDistributionTooltip: string = Constants.totalDistributionTooltip;
  extraPromptsEdit: ItExtraPrompts;
  isOneTimeTotalDist: boolean = false;
  positionLotsPhase2EnabledFsg: boolean = false;
  isEnableTotalDistSloaBsloaIt: boolean = false;
  enableUseSloaContainer: boolean = false;
  frequencySelectorModel: RadioControl = {
    title: 'Frequency',
    controlName: 'frequency',
    radios: [
      {
        value: 'onetime',
        label: ['One-time'],
      },
    ],
    validationMessage: 'Select a frequency'
  };
  closeAccountModel: RadioControl = {
    title: 'Close account after total distribution?',
    controlName: 'closeAccount',
    radios: [
      {
        value: 'yes',
        label: ["Yes—you'll have the option to request account statements and tax forms"],
      },
      {
        value:'no',
        label:["No—we'll leave the account open"]
      }

    ],
    validationMessage: 'Select yes or no'
  };
  transferTypeModel: RadioControl = {
    title: 'Transfer type',
    controlName: 'transferType',
    radios: [
      {
        value: 'cash',
        label: ['Cash'],
      },
      {
        value: 'positions',
        label: ['Positions'],
      },
      {
        value: 'positionsCash',
        label: ['Positions and cash'],
      }
    ],
    validationMessage: 'Select a Transfer type'
  };

  useStandingAuthModel: RadioControl = radioModels.useStandingAuthModel;

  standingAuthDetailsModel: CardControl = {
    title: 'Standing authorization details',
    controlName: 'standingAuthId',
    radios: [],
    validationMessage: 'Select a standing authorization'
  };
  cardModel: CardControl = {
    title: 'Select a bundled standing authorization profile',
    controlName: 'standingAuthId',
    radios: [],
    validationMessage: 'Select a bundled authorization',
  };
  isBundledOneTime: boolean;
  isRecurring: boolean = false;
  constructor(private fb: UntypedFormBuilder, private store: Store, private featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    if (typeof this.featureFlagService.featureFlags.pjlsEnabled === 'boolean') {
      combineLatest([
        this.store.select(selectHasPositionJournalLotSelection),
        this.store.select(selectUseStandingAuth)
      ]).pipe(takeUntil(this.destroy$))
        .subscribe(([hasLotSelectionFSG, standingAuth]) => {
          this.showPositionLotsFeature =
            (this.featureFlagService.featureFlags.pjlsEnabled || hasLotSelectionFSG) &&
            (this.positionLotsPhase2EnabledFsg || this.featureFlagService.featureFlags.positionLotsPhase2Enabled);
        });
    }

    const itFormGroup = this.fb.group({
      useStandingAuth: ['', Validators.required],
    });

    this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe(classifications => {
      this.classifications = classifications;
    });

    this.store.select(selectEnableUseSloaContainer).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.enableUseSloaContainer = val;
    });

    this.store.select(selectIsEnableTotalDistSloaBsloaIt).pipe(takeUntil(this.destroy$)).subscribe(enableTotalDistSloaBsloaIt => {
      this.isEnableTotalDistSloaBsloaIt = enableTotalDistSloaBsloaIt;
    });

    this.store.select(selectUseStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(useStandingAuth => {
      this.standingAuth = useStandingAuth;  
      this.setFrequencyOptions();      
    });

    this.store.select(selectHasPositionLotsPhase2).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.positionLotsPhase2EnabledFsg = val;
    });

    this.setFrequencyOptions();
    this.parentForm.setControl('extraPrompts', itFormGroup);

    this.store.select(selectHasBundled).pipe(takeUntil(this.destroy$)).subscribe(hasBundled => this.hasBundledOffer = hasBundled);

    this.store.select(selectFilteredItStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(instructions => {
      this.standingInstructions = instructions;
      this.standingAuthDetailsModel.radios = instructions?.map((template) => {
        let label = [];
        label.push({ text: 'Charles Schwab' })
        if (template?.nickname) {
          label.push({ label: 'Nickname: ', text: template?.nickname, isNewGrid: true });
        }
        if (template?.to1) {
          label.push({ label: 'Account number: ', text: template?.to1, isNewGrid: true });
        }
        label.push({ label: 'Profile type: ', text: template?.isThirdParty ? 'Third party' : 'First party', isNewGrid: true });
        return {
          value: `${template.transactionType[0]}-${template.cashieringRqstId}-${template.cashieringRqstSecondId}`,
          label: label
        }
      });

      this.cardModel.radios = instructions?.map(instruction => {
        const labelArr = [
          {text: 'To: ' + instruction.to1},
          {text: instruction.nickname},
          {text: instruction.isThirdParty ? '3rd party' : '1st party'},
        ]
        return {
          value:  `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`,
          label: labelArr
        }
      });
      
      if(this.enableUseSloaContainer && instructions && instructions.length > 0) {
        this.updateStandingAuthorizationCards(instructions);
      }
    });

    this.store.select(selectHasAccessInitiateMoneyMovement).pipe(takeUntil(this.destroy$)).subscribe(response => {
      this.hasMmInitiateAccess = response;
    });

    this.store.select(selectIsOneTimeTotalDist).pipe(takeUntil(this.destroy$)).subscribe(isOneTimeCloseAccount => {
      if (isOneTimeCloseAccount) {
        this.displayCloseAccountControl()
      }
      else {
        this.extraPromptsGroup.removeControl('closeAccount');
      }
    });

    this.store.select(selectFrequency).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isOneTimeTotalDist = info === 'onetimeTotalDist';
      this.isOneTime = info === 'onetime';
      this.isRecurring = info === 'recurring';

      if (!this.showPositionLotsFeature) {
        this.transferTypeModel.radios.splice(1, 3);
      } else if (this.positionsCashRadioButtonDisableAndHide) {
        this.transferTypeModel.radios.forEach(radio => {
          // disable and hide radio buttons and reset transfer type to trigger transfer type selection on patchValue
          if (radio.value === 'positions' || radio.value === 'positionsCash') {
            radio.disabled = true;
            radio.hide = true;
          }
        });
        this.transferType?.reset();
        this.transferType?.patchValue('cash');
        } else {
        this.transferTypeModel.radios = [
          { value: 'cash', label: ['Cash'] },
          { value: 'positions', label: ['Positions'] },
          { value: 'positionsCash', label: ['Positions and cash'] }
        ];
        this.transferTypeModel.radios.forEach(radio => {
          radio.disabled = false;
          radio.hide = false;
        });
      }

      this.frequency?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.store.dispatch(InternalTransfersActions.updateFrequencyIT());
        this.transferType?.reset();
        this.extraPromptsComplete.emit(false); // Hides the transaction details
        this.transferTypeModel.radios.forEach((radio) => {
          radio.disabled = false;
        })
        if ((this.isOneTime || this.isOneTimeTotalDist)) {
          this.extraPromptsComplete.emit(false);
          if (this.isOneTimeTotalDist && this.frequency.value !== null) {
            //setTimeout called to wait for transferType radios to render before calling patchValue
            setTimeout(() => {
              this.disableTransferTypeRadios()
              this.transferType?.patchValue('positionsCash', { emitEvent: false});
              this.extraPromptsComplete.emit(true);
            })
          }
        }
        if (this.frequency.value !== null && this.frequency?.value === Frequencies.recurring) {
          setTimeout(() => {
            this.transferType?.patchValue('cash', { emitEvent: false });
            this.extraPromptsComplete.emit(true);
          })
        }
      });
    });

    // If they select a transfer type after selecting a standing instruction and the recurring IT flag is true, then call input
    this.store.select(selectTransferType).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectStandingAuthId),
        this.store.select(selectIsFromEdit)
      ),
      filter(([_transferType, standingAuthId]) => standingAuthId !== '')
    ).subscribe(([transferType, standingAuthId, isFromEdit]) => {
      if (transferType) {
        this.callInternalTransferInput(standingAuthId, isFromEdit);
      }
    });

    this.loadPopupCallFinished$ = this.store.select(selectLoadPopupCallFinished);
    this.store.select(selectLoadPopupCallFinished).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectJournalsUseStandingAuth), this.store.select(selectStandingAuthTableStartTransactionClickedInternalTransfer)),
    ).subscribe(([loadPopupCallFinished, useStandingAuth, startTransactionClicked]) => {
      if (loadPopupCallFinished) {
        createControl(this.extraPromptsGroup, 'standingAuthId', '', [Validators.required], undefined, undefined, !startTransactionClicked);
        if (useStandingAuth === 'bundled-yes'){
          this.showBundledStandingAuthSelection = true;
        } else if (useStandingAuth === 'yes') {
          this.showStandingAuthControl = true;
        }

        if (loadPopupCallFinished && this.standingInstructions.length === 1) {
          const instruction: StandingInstruction = this.standingInstructions[0];
          this.standingAuthId?.patchValue(`${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`);
        }
      }
      this.extraPromptsComplete.emit(false);
    });

    this.store.select(selectStandingAuthId).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectJournalsUseStandingAuth),
        this.store.select(selectStandingAuthTableStartTransactionClickedInternalTransfer),
        this.store.select(selectIsFromEdit)
      )
    ).subscribe(([standingAuthId, useStandingAuth, startTransactionClicked, isFromEdit]) => {
      if (standingAuthId != '' && useStandingAuth != 'no' && !startTransactionClicked) {
        this.displayFrequencyControl(itFormGroup);
        this.frequency?.reset();
        this.removeTransferType();
      }
    });


    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.accountInfo = info;
    });

    // BUILD EXTRAPROMPTS WITH PREFILLED PROMPT WHEN PREFILLEDPROMPTSOBS EMITS OR WHEN START TRANSACTION INTERNAL TRANSFER IS SELECTED
    combineLatest([
      this.prefilledPromptsObs,
      this.store.select(selectStandingAuthTableStartTransactionClickedInternalTransfer),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([extraPrompts, startTransactionClicked]) => !!extraPrompts && startTransactionClicked),
        map(([extraprompts, _startTransactionClicked]) => extraprompts),
        distinctUntilChanged()
      )
      .subscribe(extraPrompts => {
        if (extraPrompts) {
          buildFromPrefilledForm(extraPrompts, this.extraPromptsGroup);
          this.callStandingAuthorizations(extraPrompts.useStandingAuth === 'bundled');
        }
      });

    this.store.select(selectJournalsUseStandingAuth).pipe(takeUntil(this.destroy$), filter(value => !!value), distinctUntilChanged(),).subscribe(value => {
      // If the user changes the value of useStandingAuth, hide the frequency field and set it as untouched so validation doesn't show up
      this.isBundledOneTime = value === 'bundled-no';
      if (this.extraPromptsGroup?.contains('frequency') && !this.isFromEdit) {
        let frequency = this.extraPromptsGroup?.controls?.frequency;
        frequency.setValue('');
        frequency.markAsUntouched();
      }
      let extraPrompts = this.editFormDetails?.extraPrompts as ItExtraPrompts;
      // When the user changes the value of useStandingAuth during the edit flow, reset the editForm so the values get removed
      if (this.isFromEdit && this.editFormDetails && (value !== extraPrompts.useStandingAuth && value !== `${extraPrompts.useStandingAuth}-${extraPrompts.useBundledStandingAuth}`)) {
        this.extraPromptsUpdatedInEdit.emit(); // Resets the editForm
      }
      this.extraPromptsComplete.emit(false); // Hides the transaction details

      // Reset internalTransferInfo so that service error gets cleared
      if (!this.isFromEdit) {
        this.store.dispatch(InternalTransferActions.extraPromptUpdated());
        if (value === 'yes') {
          this.showFrequencyControl = false;
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          this.callStandingAuthorizations(false);
          this.showBundledStandingAuthSelection = false;
          this.removeUseBundledStandingAuth();
          this.removeTransferType();
        } else if (value == 'no') {
          this.displayFrequencyControl(itFormGroup);
          itFormGroup.removeControl('standingAuthId');
          this.showStandingAuthControl = false;
          this.showBundledStandingAuthSelection = false;
          this.removeUseBundledStandingAuth();
          this.removeTransferType();
        } else if (value === 'bundled'){
          this.showStandingAuthControl = false;
          this.showFrequencyControl = false;
          this.showBundledStandingAuthSelection = false;
          createControl(this.extraPromptsGroup, 'useBundledStandingAuth', '', [Validators.required]);
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          this.useBundledPromptsSelected = true;
          this.removeTransferType();
        } else if (value === 'bundled-no' || value === 'bundled-recurring'){
          this.showFrequencyControl = false;
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          if (this.standingAuthId) this.extraPromptsGroup.removeControl('standingAuthId');
          this.showBundledStandingAuthSelection = false;
          createControl(this.extraPromptsGroup, 'transferType', '', [Validators.required]);
          this.transferType.reset();
          this.showTransferType = true;
          if( value === 'bundled-recurring'){
            this.extraPromptsComplete.emit(true);
          }
        }  else if (value === 'bundled-yes'){
          this.useBundledPromptsSelected = true;
          this.removeTransferType();
          this.callStandingAuthorizations(true);
        }
      } else if (this.isFromEdit) {
        if (value === 'yes' && !!extraPrompts.standingAuthId) {
          createControl(this.extraPromptsGroup, 'standingAuthId', '', [Validators.required], undefined, undefined);
          this.showStandingAuthControl = true;
          this.showFrequencyControl = false;
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          this.callStandingAuthorizations(false);
          this.showBundledStandingAuthSelection = false;
          this.removeUseBundledStandingAuth();
          this.removeTransferType();
        } else if (value == 'no') {
          this.displayFrequencyControl(itFormGroup);
          itFormGroup.removeControl('standingAuthId');
          this.showStandingAuthControl = false;
          this.showBundledStandingAuthSelection = false;
          this.removeUseBundledStandingAuth();
          this.removeTransferType();
        } else if (value === 'bundled'){
          this.showStandingAuthControl = false;
          this.showFrequencyControl = false;
          this.showBundledStandingAuthSelection = false;
          createControl(this.extraPromptsGroup, 'useBundledStandingAuth', '', [Validators.required]);
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          this.useBundledPromptsSelected = true;
          this.removeTransferType();
        } else if (value === 'bundled-no' || value === 'bundled-recurring'){
          this.showFrequencyControl = false;
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          if (this.standingAuthId) this.extraPromptsGroup.removeControl('standingAuthId');
          this.showBundledStandingAuthSelection = false;
          createControl(this.extraPromptsGroup, 'transferType', '', [Validators.required]);
          this.transferType.reset();
          this.showTransferType = true;
          if( value === 'bundled-recurring'){
            this.extraPromptsComplete.emit(true);
          }
        }  else if (value === 'bundled-yes'){
          this.showStandingAuthControl = false;
          this.showFrequencyControl = false;
          this.showBundledStandingAuthSelection = true;
          createControl(this.extraPromptsGroup, 'useBundledStandingAuth', '', [Validators.required]);
          if (this.frequency) this.extraPromptsGroup.removeControl('frequency');
          this.useBundledPromptsSelected = true;
          this.removeTransferType();
          this.callStandingAuthorizations(true);
        }
      }
    });

    this.store.select(selectJournalsUseStandingAuth).pipe(takeUntil(this.destroy$), filter(value => !!value)).subscribe(value => {
      if (value === 'bundled-no') {
        this.transferTypeModel.radios.forEach(radio => {
          radio.disabled = false;
          radio.hide = false;
        });
      } else if (value === 'bundled-recurring') {
        this.transferTypeModel.radios.forEach(radio => {
          if (radio.value === 'positions' || radio.value === 'positionsCash') {
            radio.disabled = true;
            radio.hide = true;
          }
        });
        this.transferType?.patchValue('cash');
      }
    });

    // Once standing auth call succeeds, call input and mark start transaction flow as complete
    this.store
      .select(selectItStandingAuthCallSucceeded)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectStandingAuthTableStartTransactionClickedInternalTransfer), this.prefilledPromptsObs),
        filter(([callSucceeded, startTransactionClicked, _extraPrompts]) => callSucceeded && startTransactionClicked),
        map(([_callSucceeded, _startTransactionClicked, extraPrompts]) => extraPrompts)
      ).subscribe((_extraPrompts) => {
        this.displayFrequencyControl(itFormGroup);
        this.store.dispatch(StandingAuthorizationsTableActions.startTransacionFlowComplete());
      });

    this.store.select(selectIsFromEdit).pipe(takeUntil(this.destroy$)).subscribe(isFromEdit => {
      this.isFromEdit = isFromEdit; // Subscribe to the value of isFromEdit
      if (isFromEdit && this.editFormDetails) {
        this.extraPromptsEdit = this.editFormDetails.extraPrompts as ItExtraPrompts;
        // If in the edit flow, set the values of the controls to the values from the editForm, make them visible and emit extraPromptsComplete to show the transaction details
        this.useStandingAuth.setValue(this.extraPromptsEdit.useStandingAuth, { emitEvent: false });
        if (this.extraPromptsEdit.standingAuthId) {
          this.extraPromptsGroup.addControl('standingAuthId', new UntypedFormControl(this.extraPromptsEdit.standingAuthId, [Validators.required]));
          if (this.enableUseSloaContainer && this.standingInstructions.length > 0) {
            this.updateStandingAuthorizationCards(this.standingInstructions);
          }
          if (this.extraPromptsEdit.useStandingAuth !== 'bundled'){
            this.showStandingAuthControl = true;
            this.extraPromptsComplete.emit(true);
          }
        }

        if (this.extraPromptsEdit.useStandingAuth === 'bundled'){
          createControl(this.extraPromptsGroup, 'useBundledStandingAuth', this.extraPromptsEdit.useBundledStandingAuth!, [Validators.required], undefined, undefined, true);
          this.useBundledPromptsSelected = true;
          if (this.extraPromptsEdit.useBundledStandingAuth === 'yes' && this.extraPromptsEdit.standingAuthId){
            this.showBundledStandingAuthSelection = true;
          }
          this.extraPromptsComplete.emit(true);
        }
        if (this.extraPromptsEdit.frequency) {
          createControl(this.extraPromptsGroup, 'frequency', this.extraPromptsEdit.frequency, [Validators.required]);
          this.extraPromptsComplete.emit(true);
          this.displayFrequencyControl(itFormGroup)
          if (this.extraPromptsEdit.frequency === 'onetimeTotalDist') {
            this.disableTransferTypeRadios();
          }
        }
        if (this.extraPromptsEdit.transferType){
          createControl(this.extraPromptsGroup, 'transferType', this.extraPromptsEdit.transferType, [Validators.required], undefined, undefined, true);
          this.extraPromptsComplete.emit(true);
          this.displayTransferTypeControl();
        }
        if (this.extraPromptsEdit.closeAccount){
          createControl(this.extraPromptsGroup, 'closeAccount', this.extraPromptsEdit.closeAccount, [Validators.required], undefined, undefined, true);
          this.extraPromptsComplete.emit(true);
        }
      }
    });

    this.store.select(selectInternalTransferInfo).pipe(takeUntil(this.destroy$)).subscribe(itInfo => {
      if (this.extraPromptsGroup?.valid && this.standingAuth && itInfo?.inputCallFinished) {
        this.extraPromptsComplete.emit(true);
      }
    });

    // don't emit until the last prompt has been answered and the form status has changed
    this.extraPromptsGroup?.statusChanges.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
      withLatestFrom(this.store.select(selectJournalsUseStandingAuth))
    ).subscribe(([val, useStandingAuth]) => {
      if (
        val === 'VALID' && (
          this.useStandingAuth.value.toLowerCase() === 'no' ||
          useStandingAuth === 'bundled-no' ||
          useStandingAuth === 'bundled-recurring'
        )) {
        this.extraPromptsComplete.emit(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get extraPromptsGroup(): UntypedFormGroup {
    return this.parentForm?.get('extraPrompts') as UntypedFormGroup;
  }

  get useStandingAuth(): UntypedFormControl {
    return this.extraPromptsGroup?.get('useStandingAuth') as UntypedFormControl;
  }

  get frequency(): UntypedFormControl {
    return this.extraPromptsGroup?.get('frequency') as UntypedFormControl;
  }

  get transferType(): UntypedFormControl {
    return this.extraPromptsGroup?.get('transferType') as UntypedFormControl;
  }

  get standingAuthId(): UntypedFormControl {
    return this.extraPromptsGroup?.get('standingAuthId') as UntypedFormControl;
  }

  get useBundledStandingAuth(): UntypedFormControl {
    return this.extraPromptsGroup?.get('useBundledStandingAuth') as UntypedFormControl;
  }

  get showBundledPrompts(): boolean {
    return this.hasBundledOffer;
  }

  get isInitiateUser(): boolean {
    return !!(this.hasMmInitiateAccess);
  }

  get isPositionLotsPhase2(): boolean {
    return this.featureFlagService.featureFlags.positionLotsPhase2Enabled || this.positionLotsPhase2EnabledFsg;
  }

  removeUseBundledStandingAuth(): void {
    this.useBundledPromptsSelected = false;
    if (this.useBundledStandingAuth) this.extraPromptsGroup.removeControl('useBundledStandingAuth');
  }

  private updateStandingAuthorizationCards(instructions: StandingInstruction[] | undefined): void {
    this.standingAuthCards = instructions?.map((instruction) => ({
      id: `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`,
      title: instruction.nickname || 'Charles Schwab',
      subtitle: instruction.nickname ? 'Charles Schwab' : '',
      details: [
        instruction.to1 ? `Account number: ${instruction.to1}` : '',
        `Profile type: ${instruction.isThirdParty ? 'Third party' : 'First party'}`
      ].filter(Boolean),
      selected: this.standingAuthId?.value === `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`
    })) ?? [];

    this.bundledStandingAuthCards = instructions?.map((instruction) => ({
      id: `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`,
      title: instruction.to1 ? `To: ${instruction.to1}` : '',
      subtitle: instruction.nickname || '',
      details: [instruction.isThirdParty ? '3rd party' : '1st party'].filter(Boolean),
      selected: this.standingAuthId?.value === `${instruction.transactionType[0]}-${instruction.cashieringRqstId}-${instruction.cashieringRqstSecondId}`
    })) ?? [];
  }

  onStandingAuthCardSelected(cardId: string): void {
    this.standingAuthId?.setValue(cardId);
    this.standingAuthId?.markAsTouched();
    this.standingAuthCards = this.standingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
    this.bundledStandingAuthCards = this.bundledStandingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
  }

  removeTransferType(): void {
    this.showTransferType = false;
    if (this.transferType) this.extraPromptsGroup.removeControl('transferType');
  }

  private setFrequencyOptions(): void {
    const baseRadio = [{ value: 'onetime', label: ['One-time'] }];
    const canShowTotalDist = this.isPositionLotsPhase2 && ((!this.standingAuth && !this.isEnableTotalDistSloaBsloaIt) || this.isEnableTotalDistSloaBsloaIt);
    const radios = [...baseRadio];
    if (canShowTotalDist) radios.push({ value: 'onetimeTotalDist', label: ['One-time total distribution'] });
    if (!this.classifications?.isPtrs) {
      radios.push({ value: 'recurring', label: ['Recurring'] });
      this.useBundledModel.radios.splice(
        2,
        1,
        { value: 'recurring', label: ["Recurring (using firm's bundled authorization)"] }
      );
    }
    this.frequencySelectorModel = { ...this.frequencySelectorModel, radios };
  }

  callStandingAuthorizations(isBundled: boolean): void {
    const standingAuthRequest: StandingAuthorizationsRequest = {
      subAccountId: this.accountInfo.accountId,
      masterAccountId: this.accountInfo.masterAccount,
      sloaType: isBundled ? 'bundledjournal' : 'J'
    };
    this.store.dispatch(StandingAuthorizationsActions.loadSloas(standingAuthRequest));
  }

  callInternalTransferInput(standingAuthId: string, isEditFlow: boolean): void {
    const instruction: StandingInstruction = this.standingInstructions.find(instruction => instruction.cashieringRqstId === standingAuthId.split('-')[1])!;
    if (this.standingAuthId?.valid) {
      const inputRequest: StandingAuthInputRequest = {
        masterAccountId: this.accountInfo?.masterAccount,
        subAccountId: this.accountInfo?.accountId,
        cashieringRequestId: parseInt(instruction.cashieringRqstId),
        cashieringRqstSecondId: parseInt(instruction.cashieringRqstSecondId),
        sloaPopupSelected: true,
        zaction: 'editJournal',
        frequency: 'onetime',
        isTrust: false
      };
      if (!isEditFlow) {
        this.store.dispatch(InternalTransferActions.standingAuthSelected({
          payload: inputRequest
        }));
      }
      if (this.classifications.isPtrs && !isEditFlow) {
        this.store.dispatch(PtrsActions.callPtrs({ accountId: this.accountInfo?.accountId }))
      }
    }
  }

  disableTransferTypeRadios(): void {
    this.transferTypeModel.radios.forEach((radio) => {
      if (radio.value !== "positionsCash"){
        radio.disabled = true;
      }
    })
  }

  private displayFrequencyControl( itFormGroup: UntypedFormGroup): void {
    itFormGroup.addControl('frequency', new UntypedFormControl('', Validators.required));
    this.frequency?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      // When the user changes the value of frequency during the edit flow, reset the editForm so the values get removed
      if (val) {
        this.displayTransferTypeControl();
      }
      let extraPrompts = this.editFormDetails?.extraPrompts as ItExtraPrompts;
      if (this.isFromEdit && this.editFormDetails && this.useStandingAuth.value !== extraPrompts.useStandingAuth) {
        this.extraPromptsUpdatedInEdit.emit();
      }
    });
    this.showFrequencyControl = true;
  }

  private displayTransferTypeControl(): void {
    this.extraPromptsGroup.addControl('transferType', new FormControl<ItTransferType>('', [Validators.required]));
    this.showTransferType = true;
  }

  // Returns the condition based on the enablePositionAndCashForStandingAuth value
  get positionsCashRadioButtonDisableAndHide(): boolean {
    if (this.enablePositionAndCashForStandingAuth) {
      return !this.isOneTime && !this.isOneTimeTotalDist && !this.isBundledOneTime && this.showPositionLotsFeature
    }
    else {
      return (!this.isOneTime && !this.isOneTimeTotalDist && !this.isBundledOneTime && this.showPositionLotsFeature) || ((this.isOneTime || this.isOneTimeTotalDist) && this.standingAuth)
    }
  }

  get enablePositionAndCashForStandingAuth(): boolean {
    return (this.positionLotsPhase2EnabledFsg || this.featureFlagService.featureFlags.positionLotsPhase2Enabled);
  }

  private displayCloseAccountControl(): void {
    this.extraPromptsGroup.addControl('closeAccount', new FormControl<ItCloseAccount>('', [Validators.required]));
  }
}
